/**
 * @file CompactListingCard.jsx
 * @description Masaüstü liste görünümü için kompakt ilan kartı
 *
 * TripAdvisor tarzı liste kartı bileşeni. Solda kare görsel,
 * sağda detaylı bilgiler ve aksiyon butonları. Fullscreen modunda
 * sol paneldeki ilan listesinde kullanılır.
 *
 * @module widget/components/CompactListingCard
 */

import { memo } from "react";
import { ExternalLink, MapPin, Phone, GitCompareArrows } from "lucide-react";
import { Image } from "@openai/apps-sdk-ui/components/Image";
import { formatPrice, toEmlakjetUrl, toTelHref, COLORS } from "../utils";
import { ListingBadges } from "./ListingBadges";

/**
 * Kompakt İlan Kartı Bileşeni
 *
 * Masaüstü fullscreen modunda sol panelde gösterilen
 * ilan listesi kartı. Telefon arama ve Emlakjet'te açma
 * butonları içerir.
 *
 * @component
 * @param {Object} props - Bileşen prop'ları
 * @param {Object} props.listing - İlan verisi
 * @param {number} [props.listing.id] - İlan ID'si
 * @param {string} [props.listing.title] - İlan başlığı
 * @param {string} [props.listing.location] - Konum bilgisi
 * @param {string} [props.listing.url] - Emlakjet URL'i
 * @param {number} [props.listing.price] - Fiyat
 * @param {string} [props.listing.currency] - Para birimi
 * @param {string} [props.listing.room] - Oda sayısı
 * @param {number} [props.listing.m2] - Metrekare
 * @param {string} [props.listing.phoneNumber] - İletişim telefonu
 * @param {string[]} [props.listing.images] - Görsel URL dizisi
 * @param {Function} props.onDetailClick - Detay açma callback'i (listingId)
 * @param {boolean} props.isDark - Karanlık mod aktif mi
 * @param {Function} props.openExternal - Harici link açma fonksiyonu
 * @param {boolean} [props.isInCompare] - İlan karşılaştırmada mı
 * @param {Function} [props.onToggleCompare] - Karşılaştırma toggle callback'i
 * @param {boolean} [props.canCompare] - Karşılaştırmaya eklenebilir mi (limit kontrolü)
 *
 * @returns {JSX.Element} Kompakt kart bileşeni
 *
 * @example
 * <CompactListingCard
 *   listing={listing}
 *   onDetailClick={(id) => fetchDetail(id)}
 *   isDark={theme === 'dark'}
 *   openExternal={window.openai.openExternal}
 * />
 */
function CompactListingCardInner({
  listing,
  onDetailClick,
  isDark,
  openExternal,
  isInCompare = false,
  onToggleCompare,
  canCompare = true,
}) {
  const priceLabel = formatPrice(listing?.price, listing?.currency);
  const listingId = typeof listing?.id === "number" && Number.isFinite(listing.id) ? listing.id : null;
  const listingIdStr = listingId ? String(listingId) : null;
  const imageUrl = listing?.images?.[0] || listing?.image;
  const href = toEmlakjetUrl(listing?.url);

  return (
    <div
      className={`flex gap-3 p-3 border-b cursor-pointer transition-colors ${isDark ? "bg-zinc-900 border-white/10 hover:bg-zinc-800" : "bg-white border-black/10 hover:bg-gray-50"}`}
      onClick={() => listingId && onDetailClick(listingId)}
    >
      {/* Image - Square like TripAdvisor */}
      <div className="relative w-24 h-24 rounded-lg overflow-hidden flex-shrink-0">
        {imageUrl ? (
          <Image src={imageUrl} alt={listing?.title || "İlan"} className="w-full h-full object-cover" />
        ) : (
          <div className={`w-full h-full flex items-center justify-center ${isDark ? "bg-white/5" : "bg-black/5"}`}>
            <span className={`text-xs ${isDark ? "text-white/30" : "text-black/30"}`}>Görsel yok</span>
          </div>
        )}
        {/* Badges overlay */}
        {(listing?.isOpportunity || listing?.priceTrend === "down" || listing?.badges?.length > 0) && (
          <div className="absolute top-1 left-1">
            <ListingBadges
              badges={listing?.badges}
              isOpportunity={listing?.isOpportunity}
              priceTrend={listing?.priceTrend}
              priceChangeRate={listing?.priceChangeRate}
              size="sm"
              maxBadges={1}
            />
          </div>
        )}
      </div>

      {/* Content */}
      <div className="flex-1 min-w-0 flex flex-col justify-between py-0.5">
        {/* Title */}
        <div className={`font-semibold text-sm line-clamp-2 leading-tight ${isDark ? "text-white" : "text-gray-900"}`}>
          {listing?.title || "İlan"}
        </div>

        {/* Location */}
        {listing?.location && (
          <div className={`flex items-center gap-1 text-xs ${isDark ? "text-white/50" : "text-gray-500"}`}>
            <MapPin className="h-3 w-3 flex-shrink-0" strokeWidth={1.5} />
            <span className="truncate">{listing.location}</span>
          </div>
        )}

        {/* Price */}
        <div
          className="text-base font-bold"
          style={{ color: COLORS.primary }}
        >
          {priceLabel || "—"}
        </div>

        {/* Properties as tags */}
        <div className="flex flex-wrap items-center gap-1">
          {[listing?.room, listing?.m2 ? `${listing.m2} m²` : null].filter(Boolean).map((tag, i) => (
            <span
              key={i}
              className={`text-[10px] px-1.5 py-0.5 rounded ${isDark ? "bg-white/10 text-white/60" : "bg-gray-100 text-gray-600"}`}
            >
              {tag}
            </span>
          ))}
          {listing?.phoneNumber && openExternal && (
            <button
              onClick={(e) => {
                e.stopPropagation();
                openExternal(toTelHref(listing.phoneNumber));
              }}
              className="text-[10px] px-1.5 py-0.5 rounded bg-emerald-500 text-white flex items-center gap-1 hover:bg-emerald-600 transition-colors"
            >
              <Phone className="h-3 w-3" strokeWidth={1.5} />
              Ara
            </button>
          )}
        </div>
      </div>

      {/* Action buttons */}
      <div className="flex flex-col gap-1 self-start">
        {/* Compare button */}
        {onToggleCompare && listingIdStr && (
          <button
            className={`p-1.5 rounded-md transition-colors ${
              isInCompare
                ? "text-emerald-500 hover:text-emerald-600"
                : !canCompare
                  ? isDark ? "text-white/20 cursor-not-allowed" : "text-black/20 cursor-not-allowed"
                  : isDark ? "hover:bg-white/10 text-white/40 hover:text-emerald-400" : "hover:bg-gray-100 text-gray-400 hover:text-emerald-500"
            }`}
            onClick={(e) => {
              e.stopPropagation();
              if (canCompare || isInCompare) {
                onToggleCompare(listing);
              }
            }}
            disabled={!canCompare && !isInCompare}
            aria-label={isInCompare ? "Karşılaştırmadan kaldır" : "Karşılaştır"}
            title={!canCompare && !isInCompare ? "Maksimum 3 ilan karşılaştırabilirsiniz" : isInCompare ? "Karşılaştırmadan kaldır" : "Karşılaştır"}
          >
            <GitCompareArrows
              className="h-4 w-4"
              strokeWidth={1.5}
            />
          </button>
        )}

        {/* External link icon */}
        {href && openExternal && (
          <button
            className={`p-1.5 rounded-md transition-colors ${isDark ? "hover:bg-white/10 text-white/40" : "hover:bg-gray-100 text-gray-400"}`}
            onClick={(e) => {
              e.stopPropagation();
              openExternal(href);
            }}
            aria-label="Emlakjet'te aç"
          >
            <ExternalLink className="h-4 w-4" strokeWidth={1.5} />
          </button>
        )}
      </div>
    </div>
  );
}

/**
 * Memoized CompactListingCard - liste item'ları için
 * gereksiz re-render'ları önler.
 */
export const CompactListingCard = memo(CompactListingCardInner);
export default CompactListingCard;
